<template>
  <modal
    width="920px"
    :title="modalTitle"
    @close="close"
    :read-article-url="`https://docs.bemob.com/${locale}/traffic-sources/resend-postbacks`"
  >
    <template #default>
      <div class="form-container">
        <div class="top-info">
          <div>
            {{ t('Selected') }}:
            {{ tableSelectedItems.length }}
            <ui-tooltip-icon
              class="ml-4"
              preset="help"
              :text="t('Total number of selected postback records.')"
            />
          </div>
          <div :class="{ 'top-info--isBeforeSend': dataToShow.isBeforeSend }">
            <ui-icon class="mr-6" :color="$colors.green" icon="fa-solid fa-circle" />
            {{ t('Sent') }}: {{ dataToShow.sent || 0 }}
            <ui-tooltip-icon
              class="ml-4"
              preset="help"
              :text="t('Repeatedly sent conversions to the side of the traffic source.')"
            />
          </div>
          <div :class="{ 'top-info--isBeforeSend': dataToShow.isBeforeSend }">
            <ui-icon class="mr-6" :color="$colors.gray20" icon="fa-solid fa-circle" />
            {{ t('Unsent') }}: {{ dataToShow.ready || 0 }}
            <ui-tooltip-icon
              class="ml-4"
              preset="help"
              :text="t('Conversions not yet sent to the traffic source.')"
            />
          </div>
          <div :class="{ 'top-info--isBeforeSend': dataToShow.isBeforeSend }">
            <ui-icon class="mr-6" :color="$colors.tangerine" icon="fa-solid fa-circle" />
            {{ t('Omitted') }}: {{ dataToShow.skipped || 0 }}
            <ui-tooltip-icon
              class="ml-4"
              preset="help"
              :text="t('Conversions not resent as they were already delivered.')"
            />
          </div>
          <div :class="{ 'top-info--isBeforeSend': dataToShow.isBeforeSend }">
            <ui-icon class="mr-6" :color="$colors.red" icon="fa-solid fa-circle" />
            {{ t('Failed') }}: {{ dataToShow.failed || 0 }}
            <ui-tooltip-icon
              class="ml-4"
              preset="help"
              :text="t('Conversions failed and not delivered to the traffic source.')"
            />
          </div>
        </div>
        <div class="top-info-border"></div>
        <ui-table class="table" :headers="headers" :useSearch="false" :items="tableItems">
          <template #items="{ item }">
            <div class="table-cell">
              {{ item.id }}
            </div>
            <div class="table-cell">
              {{ item.timestamp }}
            </div>
            <div class="table-cell">
              <ui-copy-input :isMini="true" :value="item.postbackUrl" />
            </div>
            <div class="table-cell">
              <ui-icon v-if="item.status" :color="circleColor(item.status)" icon="fa-solid fa-circle" />
              {{ t(item.reason || '') }}
            </div>
          </template>
        </ui-table>
        <ui-info v-if="dataToShow.isBeforeSend" class="mb-16" width="width-fit-content">
          <span v-html="t('Click the <b>Resend</b> button to retry sending the conversions.')" />
        </ui-info>
      </div>
    </template>
    <template #footer>
      <section class="justify-space-between" style="display: flex">
        <div></div>
        <div class="d-flex justify-end">
          <ui-button
            icon="fas fa-times"
            :text="dataToShow.isBeforeSend ? t('Cancel') : t('Close')"
            @click="close"
          />
          <ui-button
            v-if="dataToShow.isBeforeSend"
            button-style="accent"
            icon="fas fa-arrow-turn-right"
            :text="t('Resend')"
            @click="resendFirstTime"
            :loading="resendFirstTimeLoading"
          />
          <ui-button
            v-if="!dataToShow.isBeforeSend && !dataToShow.failed && !dataToShow.sent"
            button-style="accent"
            icon="fas fa-arrow-turn-right"
            :text="t('Retry Failed & Unsent')"
            @click="resendValid"
            :loading="resendValidLoading"
            :disabled="resendAllLoading"
          />
          <ui-button
            v-if="!dataToShow.isBeforeSend && !dataToShow.failed && !dataToShow.sent"
            button-style="accent"
            icon="fas fa-arrow-turn-right"
            :text="t('Resend All Postbacks')"
            @click="resendAll"
            :disabled="resendValidLoading"
            :loading="resendAllLoading"
          />
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useColors, useI18n } from '@/composables';
import { useTableQuery } from '@/composables/useTableQuery';
import { $emitter } from '@/services/emitter';
import { postSendPostbacksApiRequest } from '@/services/api/user-api';
import { useStore } from '@/store';

type PostbackDetail = {
  id?: string;
  timestamp?: string;
  postbackUrl?: string;
  status?: string;
  reason?: string;
};

type ResendPostbacksData = {
  res?: Record<string, unknown>;
  details?: PostbackDetail[];
  requested?: number;
  sent?: number;
  failed?: number;
  ready?: number;
  skipped?: number;
  isBeforeSend?: boolean;
};

const props = withDefaults(
  defineProps<{
    data?: ResendPostbacksData;
  }>(),
  {
    data: () => ({ res: {}, details: [] }),
  },
);

const emit = defineEmits<{
  close: [];
}>();

const { t, locale } = useI18n();
const $colors = useColors();
const store = useStore();
const tableQueryApi = useTableQuery();

const resendValidLoading = ref(false);
const resendFirstTimeLoading = ref(false);
const resendAllLoading = ref(false);
const isBeforeSend = ref(true);

const dataToShow = ref<ResendPostbacksData>({
  requested: 0,
  sent: 0,
  failed: 0,
  res: {},
  details: [],
});

const tableSelectedItems = computed(() => tableQueryApi.tableSelectedItems.value);

const selectedItemsIds = computed(() =>
  (tableSelectedItems.value as Array<{ DT_RowId: string }>).map((item) => item.DT_RowId),
);

const modalTitle = computed(() => {
  if (isBeforeSend.value) {
    return `${t('Resend Postbacks')}: ${t('Review Records')}`;
  }
  return `${t('Resend Postbacks')}: ${t('Results')}`;
});

const tableItems = computed(() => {
  if (isBeforeSend.value) {
    return (tableSelectedItems.value as Record<string, string>[]).map((item) => ({
      postbackUrl: item['postbacks-url'],
      timestamp: item['postbacks-timestamp'],
      id: item['postbacks-clickid'],
    }));
  }
  return dataToShow.value.details || [];
});

const headers = computed(() => [
  {
    text: t('Id'),
    value: 'id',
    sortable: false,
    help: t('Unique Click ID generated by BeMob.'),
  },
  {
    text: t('Timestamp'),
    value: 'timestamp',
    sortable: false,
    help: t('Date and time of the first attempt to send the conversion to the traffic source.'),
  },
  {
    text: t('Postback URL'),
    value: 'postbackUrl',
    sortable: false,
    help: t('Postback URL used to send the conversion.'),
  },
  {
    text: t('Details'),
    value: 'details',
    sortable: false,
    help: t('Status of the resent conversion with color indicator.'),
  },
]);

const circleColor = (status: string) => {
  switch (status) {
    case 'sent':
      return $colors.green;
    case 'skipped':
      return $colors.tangerine;
    case 'ready':
      return $colors.gray20;
    case 'failed':
      return $colors.red;
  }
};

const showResendToasts = (res: ResendPostbacksData) => {
  const { requested, sent, failed } = res;
  if (requested === sent) {
    $emitter.emit(
      'toast-success',
      t('Conversions sent successfully. You can close this window and continue.'),
    );
  }
  if (requested !== sent && requested !== failed) {
    $emitter.emit(
      'toast-warning',
      t(
        'The request was resent, but an error occurred during sending.<br> Check Details for the error reason. <br>Do not resend until the issue is fixed.',
      ),
    );
  }
  if (requested === failed) {
    $emitter.emit(
      'toast-error',
      t(
        'Some conversions were sent successfully, but errors occurred for others during sending. <br>Check Details for the error reason.<br>Do not resend until the issue is fixed.',
      ),
    );
  }
};

const close = () => {
  if (!isBeforeSend.value) {
    $emitter.emit('reload-report');
  }
  emit('close');
};

const resendValid = () => {
  store.setLoading(true);
  resendValidLoading.value = true;
  postSendPostbacksApiRequest({ ids: selectedItemsIds.value, mode: 'valid' })
    .then((res) => {
      dataToShow.value = res as ResendPostbacksData;
    })
    .finally(() => {
      store.setLoading(false);
      resendValidLoading.value = false;
    });
};

const resendFirstTime = () => {
  store.setLoading(true);
  resendFirstTimeLoading.value = true;
  postSendPostbacksApiRequest({ ids: selectedItemsIds.value, mode: 'default' })
    .then((res) => {
      dataToShow.value = res as ResendPostbacksData;
      isBeforeSend.value = false;
      showResendToasts(res as ResendPostbacksData);
    })
    .finally(() => {
      store.setLoading(false);
      resendFirstTimeLoading.value = false;
    });
};

const resendAll = () => {
  store.setLoading(true);
  resendAllLoading.value = true;
  postSendPostbacksApiRequest({ ids: selectedItemsIds.value, mode: 'all' })
    .then((res) => {
      dataToShow.value = res as ResendPostbacksData;
      showResendToasts(res as ResendPostbacksData);
    })
    .finally(() => {
      resendAllLoading.value = false;
      store.setLoading(false);
    });
};

onMounted(() => {
  dataToShow.value = props.data ?? { res: {}, details: [] };
  isBeforeSend.value = !!props.data?.isBeforeSend;
});
</script>

<style lang="scss" scoped>
.top-info {
  display: flex;
  gap: 20px;
  max-width: 700px;
  padding: 0 0 6px !important;
  width: 100%;
  justify-content: space-between;
  @media (max-width: 900px) {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  div {
    height: 30px;
    display: flex;
    font-size: 20px;
    place-content: center;
    white-space: nowrap;
    align-items: center;
    &:lang(ru) {
      font-size: 18px;
    }
  }
}

.top-info--isBeforeSend {
  opacity: 0.2;
}

.top-info-border {
  border-bottom: 1px solid var(--color-gray10);
  width: 100%;
  margin-bottom: 30px;
}

.table {
  width: 100%;
  overflow-x: auto;
}

:deep(.table-header),
:deep(.table-body) {
  min-width: 800px;
  max-height: 260px !important;
  .table-cell {
    padding-top: 15px;
    padding-bottom: 15px;
    box-sizing: border-box;
  }
  .table-cell:nth-child(1) {
    flex-grow: 0;
    flex-basis: 200px;
    flex-shrink: 1;
    word-break: break-all;
    padding-right: 20px;
    text-align: left;
  }
  .table-cell:nth-child(2) {
    word-break: break-all;
    padding-right: 20px;
    text-align: left;
    flex-grow: 0;
    flex-basis: 90px;
    &:lang(ru) {
      flex-basis: 120px !important;
    }
    &:lang(es) {
      flex-basis: 120px !important;
    }
  }
  .table-cell:nth-child(3) {
    flex: 2;
    word-break: break-all;
    padding-right: 20px;
    text-align: left;
  }
  .table-cell:nth-child(4) {
    flex: 2;
    word-break: break-all;
    text-align: left;
  }
}
</style>
